import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /** @type {AbortController | undefined} */
  #accordionController;

  /** @type {AbortController | undefined} */
  #clickDelegationController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupAccordionBehavior();
    this.#setupClickDelegation();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#accordionController?.abort();
    this.#clickDelegationController?.abort();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Close other top-level submenus so only one is open at a time
    this.#closeSiblingTopLevelSubmenus(details);

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Close sibling top-level submenus so only one is open at a time (accordion behavior)
   * @param {HTMLDetailsElement} openDetails - The details element that is being opened
   */
  #closeSiblingTopLevelSubmenus(openDetails) {
    const listItem = openDetails.closest('li');
    if (!listItem) return;

    const topLevelMenu = listItem.closest('.menu-drawer__menu.has-submenu');
    if (!topLevelMenu) return;

    // Only apply to the main/top-level menu (first menu in navigation)
    const nav = topLevelMenu.closest('.menu-drawer__navigation');
    if (!nav || topLevelMenu !== nav.querySelector('.menu-drawer__menu.has-submenu')) return;

    const listItems = topLevelMenu.querySelectorAll(':scope > li');
    listItems.forEach((li) => {
      const accordionDetails = li.querySelector('accordion-custom > details');
      const menuContainerDetails = li.querySelector('details.menu-drawer__menu-container');
      const siblingDetails = accordionDetails || menuContainerDetails;

      if (siblingDetails && siblingDetails !== openDetails) {
        const isOpen =
          siblingDetails.hasAttribute('open') || siblingDetails.classList.contains('menu-open');
        if (isOpen) {
          reset(siblingDetails);
          siblingDetails.querySelector('summary')?.setAttribute('aria-expanded', 'false');
        }
      }
    });
  }

  /**
   * Listen for accordion toggle events so only one top-level submenu is open at a time
   */
  #setupAccordionBehavior() {
    this.#accordionController = new AbortController();
    const { signal } = this.#accordionController;

    const topLevelMenu = this.querySelector('.menu-drawer__navigation .menu-drawer__menu.has-submenu');
    if (!topLevelMenu) return;

    topLevelMenu.addEventListener(
      'toggle',
      (event) => {
        const details = event.target;
        if (!(details instanceof HTMLDetailsElement)) return;

        // Only handle accordion details (nested inside accordion-custom), not menu-container details
        if (!details.closest('accordion-custom')) return;

        // Only act when opening (details.open is true after toggle)
        if (!details.open) return;

        this.#closeSiblingTopLevelSubmenus(details);
      },
      { capture: true, signal }
    );
  }

  /**
   * Use click delegation to close sibling submenus - handles both standard theme menu
   * and Qikify menu. Runs in capture phase before other handlers.
   */
  #setupClickDelegation() {
    this.#clickDelegationController = new AbortController();
    const { signal } = this.#clickDelegationController;

    const menuDrawer = this.querySelector('.menu-drawer');
    if (!menuDrawer) return;

    menuDrawer.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;

        // Handle Qikify menu structure - close ALL others BEFORE click propagates to Qikify
        const qikifyNav =
          this.querySelector('.menu-drawer__navigation.tmenu_app') ||
          (() => {
            const nav = this.querySelector('.menu-drawer__navigation');
            return nav?.querySelector('.tmenu_item_level_0') ? nav : null;
          })();
        if (qikifyNav) {
          // Find the top-level item that was clicked (link is a.tmenu_item_link inside container)
          const clickedItem = this.#getQikifyTopLevelItem(qikifyNav, event.target);
          if (clickedItem) {
            // Close ALL other top-level items first (synchronously in capture phase, before Qikify runs)
            // Then Qikify will open only the clicked item
            this.#closeAllQikifySubmenusExcept(clickedItem);
          }
          return;
        }

        // For standard menu: close siblings in capture phase BEFORE open() runs
        const clickedDetails =
          event.target.closest('details.menu-drawer__menu-container') ||
          event.target.closest('accordion-custom details');
        if (clickedDetails) {
          this.#closeSiblingTopLevelSubmenus(clickedDetails);
        }
      },
      { capture: true, signal }
    );
  }

  /**
   * Find the top-level Qikify menu item that contains the clicked element
   * @param {Element} qikifyNav - The nav container
   * @param {Element} target - The clicked element
   * @returns {Element | null} The top-level item or null
   */
  #getQikifyTopLevelItem(qikifyNav, target) {
    const topLevelItems = Array.from(qikifyNav.querySelectorAll('.tmenu_item_level_0')).filter(
      (item) =>
        item.parentElement === qikifyNav || item.parentElement?.parentElement === qikifyNav
    );
    return topLevelItems.find((item) => item.contains(target)) || target.closest('.tmenu_item_level_0');
  }

  /**
   * Close all Qikify top-level submenus except the one being opened.
   * Runs before Qikify's handler, so we close all first, then Qikify opens only the clicked item.
   * @param {Element} openItem - The tmenu_item_level_0 that was clicked (will be opened by Qikify)
   */
  #closeAllQikifySubmenusExcept(openItem) {
    const qikifyNav =
      this.querySelector('.menu-drawer__navigation.tmenu_app') ||
      this.querySelector('.menu-drawer__navigation');
    if (!qikifyNav?.querySelector('.tmenu_item_level_0')) return;

    // Get top-level items (direct children of nav or of first ul in nav)
    const topLevelItems = Array.from(qikifyNav.querySelectorAll('.tmenu_item_level_0')).filter(
      (item) =>
        item.parentElement === qikifyNav || item.parentElement?.parentElement === qikifyNav
    );

    topLevelItems.forEach((item) => {
      if (item === openItem) return;

      // Remove common open-state classes used by Qikify to collapse submenus
      item.classList.remove('tmenu_open', 'open', 'tmenu_expanded', 'expanded', 'opened');

      // Hide submenus - Qikify uses tmenu_submenu classes
      const submenus = item.querySelectorAll(
        '.tmenu_submenu, ul[class*="tmenu_submenu"], [class*="tmenu_submenu"]'
      );
      submenus.forEach((sub) => {
        if (sub instanceof HTMLElement) {
          sub.style.setProperty('display', 'none', 'important');
        }
      });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
